import React, { FC, useState, useEffect, useContext } from 'react';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';

import BackflowDeviceForm from './BackflowDeviceForm';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { backflowJobItemRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowJobItem } from '../../model';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowDeviceEditProps {
  backflowJobItem: BackflowJobItem;
  onSave: () => void;
  onCancel: () => void;
}

const BackflowDeviceEdit: FC<BackflowDeviceEditProps> = ({ backflowJobItem, onSave, onCancel }) => {
  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<BackflowJobItem>(backflowJobItem);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>('');

  useEffect(() => {
    if (backflowJobItem?.id) {
      setFormValues(backflowJobItem);
    }
  }, [backflowJobItem]);

  const handleUpdate = async () => {
    const validate = validateRequiredFields(formValues, backflowJobItemRequiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    setLoading(true);

    try {
      await axios.put(`/backflows/backflow_job_items/${backflowJobItem.id}`, {
        backflow_job_item: formValues,
      });

      setSnackBarMessage('Updated Backflow Device Info');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);
      setErrorMessage('');
      onSave();
    } catch (error) {
      setErrorMessage('Error Updating Backflow Device Info');
      setSnackBarMessage('Error Updating Backflow Device Info');
      handleSnackbarAlertSeverityChange('error');
      setSnackBarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BackflowDeviceForm formValues={formValues} setFormValues={setFormValues} />
            </Grid>
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage || validationErrors} />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleUpdate}>
                Update
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </LoadingWrapper>
  );
};

export default BackflowDeviceEdit;
